import { Accessor, createSignal, JSX, Setter, Show } from "solid-js";
import { zIndexes } from "~/z_indexes";
import { CoinsPillOrBranding } from "./coinspill_or_branding";
import { Mode } from "~/types";
import { PhosphorIcon } from "~/widgets/icons";
import { HeaderMenu } from "./header_menu";
import { A } from "@solidjs/router";
import { openIntercom } from "~/utils/third_party/intercom";
import { axisLogo, HubbleFullSvg } from "~/assets/assets";
import { clientIdConfig } from "~/data/partner_config";

export const axisPrimaryColor = "#97144d";

type AxisHeaderProps = {
  userName?: string;
  mode: Mode;
  isLoggedIn: boolean;
  coins: number;
  showCoins?: Accessor<boolean>;
  setRefreshCoins?: Setter<boolean>;
  isSearchActive?: Accessor<boolean>;
  setIsSearchActive?: Setter<boolean>;
  isDropdownOpen: Accessor<boolean>;
  setIsDropdownOpen: Setter<boolean>;
  setRefreshLatestBalance?: Setter<boolean>;
  buildSearchBar?: () => JSX.Element;
  clientId: string;
  hideSearchBar?: boolean;
  showMyGiftCards?: boolean;
};

export const AxisHeader = ({
  userName,
  isLoggedIn,
  coins,
  showCoins,
  isSearchActive,
  setIsSearchActive,
  isDropdownOpen,
  setIsDropdownOpen,
  buildSearchBar,
  clientId,
  hideSearchBar,
  showMyGiftCards = true,
}: AxisHeaderProps) => {
  const [mobileButtonRef, setMobileButtonRef] = createSignal<HTMLDivElement>();

  const tncUrl =
    clientId === clientIdConfig.indriyaClientId
      ? "/tnc/axisbank-indriya"
      : "/tnc/axisbank";

  return (
    <div
      class={`${"sticky"} top-0 h-[120px] w-full border-b-0  bg-baseTertiaryLight  sm:h-[96px]  sm:border-b-0 sm:pb-2`}
      style={{
        "z-index": zIndexes.header,
      }}
    >
      <div
        class={`fixed  left-0 right-0  h-full w-full bg-baseDark transition-all duration-300 darkTheme:bg-baseLight md:top-24 ${isSearchActive?.() ? "opacity-40" : "pointer-events-none opacity-0"} ${isSearchActive?.() ? "top-28" : "top-24"}`}
      />
      <div class="flex h-full w-full flex-col ">
        <AxisHeaderRowWithBurgundyBackground />
        <div class="absolute mt-1 flex w-full flex-row items-center justify-center bg-baseTertiaryLight md:hidden">
          <Show when={isSearchActive?.()}>{buildSearchBar?.()}</Show>
        </div>
        <div class="flex md:hidden">
          <DropDown />
        </div>
      </div>
    </div>
  );

  function AxisHeaderRowWithBurgundyBackground() {
    return (
      <>
        <div class="flex h-full w-full flex-row">
          {/* left side space filler div */}
          <div
            class={`flex h-full w-full flex-1 `}
            style={{
              "background-color": axisPrimaryColor,
            }}
          ></div>

          {/* main content div, will be center aligned, with some max width */}
          <div class="relative flex w-full max-w-[1200px] flex-row">
            {/* first half of the strip */}
            <div
              class="relative flex h-full w-[40%] flex-row items-center justify-start gap-1  pl-4 sm:w-[20%] sm:justify-end sm:pr-[60px]"
              style={{
                "background-color": axisPrimaryColor,
              }}
            >
              <A href="/" class="">
                <img
                  id="axis-bank-header-logo"
                  src={axisLogo}
                  alt="axis bank logo"
                  class="h-6 sm:h-7"
                />
              </A>

              {/* additonal strip to cover the gap between the two halves */}
              <div
                class="absolute bottom-0 right-[-1px] top-0  w-[2px] "
                style={{
                  "background-color": axisPrimaryColor,
                }}
              ></div>
            </div>

            {/* second half of the strip */}
            <div class="flex h-full flex-1 flex-col items-start">
              <div
                class={`h-[45%] w-full `}
                style={{
                  "background-color": axisPrimaryColor,
                }}
              >
                <div class="hidden sm:block">
                  <DesktopButtons />
                </div>
              </div>
              <div class="relative h-[55%]">
                <AxisStripBottomCurveSvg />
                {/* additonal strip to cover the gap between the two halves */}
                <div
                  class="absolute left-0 right-[4px] top-[-1px]   h-[2px] "
                  style={{
                    "background-color": axisPrimaryColor,
                  }}
                ></div>
              </div>
            </div>

            {/* absolute - search bar and coins */}
            <Show when={isLoggedIn}>
              <SearchBarAndCoins />
            </Show>
          </div>

          {/* right side space filler div */}
          <div
            class={`flex h-[45%] w-full flex-1 `}
            style={{
              "background-color": axisPrimaryColor,
            }}
          ></div>
        </div>
      </>
    );
  }

  function DropDown() {
    return (
      <>
        <div
          ref={setMobileButtonRef}
          style={{
            color: axisPrimaryColor,
          }}
          class="cursor-pointer px-4 py-2"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setIsDropdownOpen?.(!isDropdownOpen?.());
          }}
        >
          <Show
            when={isDropdownOpen?.()}
            fallback={<PhosphorIcon name="list" fontSize={28} size={"bold"} />}
          >
            <PhosphorIcon name="x" fontSize={28} size={"bold"} />
          </Show>
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <HeaderMenu
            isLoggedIn={isLoggedIn}
            mode={"rnr"}
            isDropdownOpen={isDropdownOpen!}
            setIsDropdownOpen={setIsDropdownOpen!}
            clientId={() => clientId}
            showMyGiftCards={() => showMyGiftCards}
            partnerPortalTncUrl={tncUrl}
            fAQsInHeader={[]}
            headerButtonRef={mobileButtonRef}
          />
        </div>
      </>
    );
  }

  function DesktopButtons() {
    return (
      <div class="flex justify-end gap-5 py-1 md:mr-8 xl:mr-0 xl:py-3">
        <p
          class="cursor-pointer text-mediumBold text-white"
          onClick={() => {
            openIntercom();
          }}
        >
          Contact support
        </p>
        <A href={tncUrl}>
          <p class="cursor-pointer text-mediumBold text-white">
            Terms & conditions
          </p>
        </A>
        <Show when={isLoggedIn}>
          <Show when={showMyGiftCards}>
            <A href="/profile">
              <p class="cursor-pointer text-mediumBold text-white">
                My gift cards
              </p>
            </A>
          </Show>
          <A href="/logout">
            <p class="cursor-pointer text-mediumBold text-white">Logout</p>
          </A>
        </Show>
      </div>
    );
  }

  function SearchBarAndCoins() {
    return (
      <>
        <div class="hidden sm:block">
          {/* <div class="absolute bottom-0 left-1/2 -translate-x-1/2 transform">
            {buildSearchBar?.()}
          </div> */}
          <div
            class="absolute bottom-0 right-0"
            classList={{
              "mt-1": showCoins?.() ?? false,
            }}
          >
            {buildCoinsPillOrBranding()}
          </div>
        </div>
        <div class="block sm:hidden">
          <div class="absolute bottom-0 right-4 flex flex-row">
            {buildCoinsPillOrBranding()}
            <Show when={showCoins?.() && !hideSearchBar}>
              <div
                class="ml-2 flex h-8 w-8 items-center justify-center rounded-full border border-baseTertiaryDark align-middle"
                onClick={() => {
                  setIsDropdownOpen?.(false);
                  setIsSearchActive?.(!isSearchActive?.());
                }}
              >
                <PhosphorIcon
                  name="magnifying-glass"
                  fontSize={14}
                  class="text-textNormal"
                  size="bold"
                />
              </div>
            </Show>
          </div>
        </div>
      </>
    );
  }

  function buildCoinsPillOrBranding() {
    return (
      <CoinsPillOrBranding
        mode={"rnr"}
        showRedeemCodeCta={false}
        showCoins={showCoins}
        userName={userName}
        isRedeemCodeEnabled={false}
        coins={coins}
        showBranding={true}
        showVerticalBranding={false}
        isClientAxis={true}
        buildBranding={buildBranding}
      />
    );
  }

  function buildBranding() {
    return (
      <div class="flex flex-row items-center justify-center pt-4 sm:flex-col sm:items-end sm:pt-0">
        <span
          class={` text-[10px] font-semibold text-baseDark sm:text-[8.12px] `}
        >
          Powered by
        </span>
        <div class="w-[8px]" />
        <div class="h-[2px]" />
        <HubbleFullSvg class="h-[11px] fill-baseDark" />
      </div>
    );
  }

  function AxisStripBottomCurveSvg() {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 50 48"
        class="h-full w-auto"
        fill="none"
      >
        <path
          d="M0.651883 48H0V0H50C42.9619 0 36.538 4.00744 33.4433 10.3286L21.3091 35.1134C17.4479 43.0001 9.43301 48 0.651883 48Z"
          fill={axisPrimaryColor}
        />
      </svg>
    );
  }
};
